import { useEffect, useState, useMemo } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  MessageSquareWarning, Lightbulb, Send, Shield, CheckCircle2,
  Clock, Search, Trash2, ChevronDown, ChevronUp, Inbox, AlertCircle,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatErr } from "@/lib/api";
import DashboardShell, { ColorStat } from "@/components/DashboardShell";

const STATUS_META = {
  new:      { label: "New",      cls: "bg-rose/10 text-rose",         icon: AlertCircle },
  reviewed: { label: "Reviewed", cls: "bg-amber-500/10 text-amber-600", icon: Clock },
  resolved: { label: "Resolved", cls: "bg-emerald-500/10 text-emerald-600", icon: CheckCircle2 },
};

export default function Feedback() {
  const { user, loading } = useAuth();
  const isAdmin = user?.role === "admin";
  const [tab, setTab] = useState(isAdmin ? "inbox" : "submit");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterType, setFilterType] = useState("all");
  const [filterStatus, setFilterStatus] = useState("all");
  const [search, setSearch] = useState("");
  const [form, setForm] = useState({ type: "suggestion", subject: "", message: "", anonymous: false });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState({});

  useEffect(() => { if (user) refresh(); /* eslint-disable-next-line */ }, [user, isAdmin]);

  const filtered = useMemo(() => {
    return items.filter(f => {
      if (filterType !== "all" && f.type !== filterType) return false;
      if (filterStatus !== "all" && f.status !== filterStatus) return false;
      if (search) {
        const s = search.toLowerCase();
        return (f.subject || "").toLowerCase().includes(s) ||
               (f.message || "").toLowerCase().includes(s) ||
               (f.user_name || "").toLowerCase().includes(s);
      }
      return true;
    });
  }, [items, filterType, filterStatus, search]);

  const refresh = async () => {
    try {
      if (isAdmin) {
        const [rows, s] = await Promise.all([
          api.get("/feedback").then(r => r.data),
          api.get("/feedback/stats").then(r => r.data),
        ]);
        setItems(rows); setStats(s);
      } else {
        const rows = await api.get("/feedback/my").then(r => r.data);
        setItems(rows);
      }
    } catch { /* silent */ }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-ivory"><div className="w-10 h-10 border-2 border-aubergine/20 border-t-aubergine rounded-full animate-spin" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const submit = async (e) => {
    e.preventDefault();
    const subj = form.subject.trim(); const msg = form.message.trim();
    if (!subj || !msg) { toast.error("Please fill subject and message"); return; }
    setBusy(true);
    try {
      await api.post("/feedback", form);
      toast.success(form.type === "complaint" ? "Complaint submitted. Admin will review it privately." : "Suggestion submitted. Thank you!");
      setForm({ type: "suggestion", subject: "", message: "", anonymous: false });
      setTab("history");
      refresh();
    } catch (er) {
      toast.error(formatErr(er.response?.data?.detail) || "Failed to submit");
    } finally { setBusy(false); }
  };

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/feedback/${id}`, { status });
      toast.success("Status updated");
      refresh();
    } catch { toast.error("Failed"); }
  };

  const saveNotes = async (id, notes) => {
    try {
      await api.patch(`/feedback/${id}`, { admin_notes: notes });
      toast.success("Notes saved");
      refresh();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this feedback permanently?")) return;
    try {
      await api.delete(`/feedback/${id}`);
      toast.success("Deleted");
      refresh();
    } catch { toast.error("Failed"); }
  };


  return (
    <DashboardShell
      role={isAdmin ? "admin" : "member"}
      title={isAdmin ? "Feedback Inbox" : "Feedback Box"}
      subtitle={isAdmin ? "Private complaints & suggestions from members" : "Share complaints or suggestions — read privately by admin"}
    >
      <div data-testid="feedback-page">
        {/* Privacy banner */}
        <div className="flex items-start gap-3 rounded-xl bg-aubergine/[0.04] border border-aubergine/[0.08] p-4 mb-6">
          <div className="w-9 h-9 rounded-lg bg-white flex items-center justify-center text-aubergine shrink-0"><Shield className="w-4 h-4" /></div>
          <div>
            <p className="text-[13px] text-aubergine font-medium">Confidential channel</p>
            <p className="text-xs text-plumSoft/80 mt-0.5">
              {isAdmin
                ? "Only admins can view this inbox. Anonymous submissions hide the member's identity even from you."
                : "Only the WOWSHE admin can read what you submit here. Fellow members will never see it. Optionally submit anonymously."}
            </p>
          </div>
        </div>

        {/* Admin stats */}
        {isAdmin && stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <ColorStat icon={Inbox} label="Total" value={stats.total} tone="purple" />
            <ColorStat icon={AlertCircle} label="New" value={stats.new} tone="rose" />
            <ColorStat icon={MessageSquareWarning} label="Complaints" value={stats.complaints} tone="orange" />
            <ColorStat icon={Lightbulb} label="Suggestions" value={stats.suggestions} tone="gold" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-6">
          {isAdmin ? (
            <TabBtn active={tab === "inbox"} onClick={() => setTab("inbox")} testid="fb-tab-inbox">Inbox</TabBtn>
          ) : (
            <>
              <TabBtn active={tab === "submit"} onClick={() => setTab("submit")} testid="fb-tab-submit">Submit New</TabBtn>
              <TabBtn active={tab === "history"} onClick={() => setTab("history")} testid="fb-tab-history">My Submissions ({items.length})</TabBtn>
            </>
          )}
        </div>

        {/* MEMBER: submit form */}
        {!isAdmin && tab === "submit" && (
          <form onSubmit={submit} className="bg-white rounded-2xl border border-aubergine/[0.08] p-6 md:p-8 max-w-3xl">
            <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-2">Type</label>
            <div className="grid grid-cols-2 gap-3 mb-6">
              <TypeCard active={form.type === "suggestion"} onClick={() => setForm({ ...form, type: "suggestion" })} icon={Lightbulb} title="Suggestion" desc="Idea to improve WOWSHE" testid="fb-type-suggestion" />
              <TypeCard active={form.type === "complaint"} onClick={() => setForm({ ...form, type: "complaint" })} icon={MessageSquareWarning} title="Complaint" desc="Something that needs attention" testid="fb-type-complaint" />
            </div>

            <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">Subject *</label>
            <input
              data-testid="fb-subject"
              maxLength={200}
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-aubergine/[0.08] text-[14px] text-aubergine focus:border-aubergine/40 focus:outline-none mb-5"
              placeholder={form.type === "complaint" ? "e.g. Issue with event registration" : "e.g. Add a mentorship track"}
            />

            <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">
              Details * <span className="text-plumSoft/50 normal-case tracking-normal">({form.message.length}/4000)</span>
            </label>
            <textarea
              data-testid="fb-message"
              maxLength={4000}
              rows={7}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className="w-full px-4 py-3 rounded-xl bg-[#FAF8F5] border border-aubergine/[0.08] text-[14px] text-aubergine focus:border-aubergine/40 focus:outline-none mb-5"
              placeholder="Tell the admin exactly what happened or what you'd like to see. Be as specific as helpful."
            />

            <label className="flex items-center gap-2.5 mb-6 cursor-pointer select-none">
              <input
                type="checkbox"
                data-testid="fb-anonymous"
                checked={form.anonymous}
                onChange={(e) => setForm({ ...form, anonymous: e.target.checked })}
                className="w-4 h-4 rounded border-aubergine/30 text-aubergine focus:ring-aubergine/40"
              />
              <span className="text-[13px] text-plumSoft">
                Submit anonymously — admin will not see your name, email or chapter
              </span>
            </label>

            <div className="flex items-center gap-3">
              <button
                data-testid="fb-submit"
                type="submit"
                disabled={busy}
                className="inline-flex items-center gap-2 bg-aubergine text-ivory px-6 py-3 rounded-full text-[13px] tracking-wide hover:bg-aubergine/90 transition-colors disabled:opacity-50"
              >
                <Send className="w-4 h-4" />
                {busy ? "Submitting…" : `Submit ${form.type === "complaint" ? "Complaint" : "Suggestion"}`}
              </button>
              <p className="text-xs text-plumSoft/60">Read privately by admin only.</p>
            </div>
          </form>
        )}

        {/* MEMBER: history */}
        {!isAdmin && tab === "history" && (
          <FeedbackList
            items={items}
            emptyMsg="You haven't submitted any feedback yet."
            isAdmin={false}
            expanded={expanded}
            setExpanded={setExpanded}
          />
        )}

        {/* ADMIN: inbox */}
        {isAdmin && (
          <>
            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-5">
              <div className="relative flex-1 min-w-[220px] max-w-md">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-plumSoft/60" />
                <input
                  data-testid="fb-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search subject, message, member…"
                  className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-aubergine/[0.08] text-[13px] text-aubergine focus:border-aubergine/40 focus:outline-none"
                />
              </div>
              <SelectPill v={filterType} onChange={setFilterType} testid="fb-filter-type" opts={[
                { v: "all", l: "All types" },
                { v: "complaint", l: "Complaints" },
                { v: "suggestion", l: "Suggestions" },
              ]} />
              <SelectPill v={filterStatus} onChange={setFilterStatus} testid="fb-filter-status" opts={[
                { v: "all", l: "All statuses" },
                { v: "new", l: "New" },
                { v: "reviewed", l: "Reviewed" },
                { v: "resolved", l: "Resolved" },
              ]} />
            </div>

            <FeedbackList
              items={filtered}
              emptyMsg={items.length === 0 ? "No feedback submitted yet." : "No feedback matches your filters."}
              isAdmin
              expanded={expanded}
              setExpanded={setExpanded}
              onStatus={setStatus}
              onNotes={saveNotes}
              onDelete={remove}
            />
          </>
        )}
      </div>
    </DashboardShell>
  );
}

function TabBtn({ active, onClick, children, testid }) {
  return (
    <button
      data-testid={testid}
      onClick={onClick}
      className={`px-5 py-2 rounded-full text-xs uppercase tracking-[0.14em] border transition-colors ${
        active ? "bg-aubergine text-ivory border-aubergine" : "border-aubergine/20 text-plum hover:bg-aubergine/[0.04]"
      }`}
    >{children}</button>
  );
}

function TypeCard({ active, onClick, icon: Icon, title, desc, testid }) {
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={onClick}
      className={`text-left rounded-xl border p-4 flex items-start gap-3 transition-colors ${
        active
          ? "border-aubergine bg-aubergine/[0.04]"
          : "border-aubergine/[0.08] bg-white hover:border-aubergine/25"
      }`}
    >
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${active ? "bg-aubergine text-ivory" : "bg-aubergine/[0.06] text-aubergine"}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-[13px] font-medium text-aubergine">{title}</p>
        <p className="text-[11px] text-plumSoft/70 mt-0.5">{desc}</p>
      </div>
    </button>
  );
}

function SelectPill({ v, onChange, opts, testid }) {
  return (
    <select
      data-testid={testid}
      value={v}
      onChange={(e) => onChange(e.target.value)}
      className="px-4 py-2.5 rounded-full bg-white border border-aubergine/[0.08] text-[13px] text-aubergine focus:border-aubergine/40 focus:outline-none cursor-pointer"
    >
      {opts.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

function FeedbackList({ items, emptyMsg, isAdmin, expanded, setExpanded, onStatus, onNotes, onDelete }) {
  if (items.length === 0) {
    return <div className="rounded-2xl bg-white border border-aubergine/[0.08] p-10 text-center text-plumSoft">{emptyMsg}</div>;
  }
  return (
    <div className="space-y-3">
      {items.map(f => (
        <FeedbackRow
          key={f.id}
          f={f}
          isAdmin={isAdmin}
          open={!!expanded[f.id]}
          toggle={() => setExpanded({ ...expanded, [f.id]: !expanded[f.id] })}
          onStatus={onStatus}
          onNotes={onNotes}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
}

function FeedbackRow({ f, isAdmin, open, toggle, onStatus, onNotes, onDelete }) {
  const meta = STATUS_META[f.status] || STATUS_META.new;
  const StatusIcon = meta.icon;
  const TypeIcon = f.type === "complaint" ? MessageSquareWarning : Lightbulb;
  const typeCls = f.type === "complaint" ? "bg-rose/10 text-rose" : "bg-amber-500/10 text-amber-600";
  const [notes, setNotes] = useState(f.admin_notes || "");
  useEffect(() => { setNotes(f.admin_notes || ""); }, [f.admin_notes]);

  return (
    <div data-testid={`fb-row-${f.id}`} className="bg-white rounded-2xl border border-aubergine/[0.08] overflow-hidden">
      <button
        type="button"
        onClick={toggle}
        className="w-full text-left px-5 py-4 flex items-start gap-4 hover:bg-aubergine/[0.02] transition-colors"
      >
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${typeCls}`}>
          <TypeIcon className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${typeCls}`}>{f.type}</span>
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest ${meta.cls}`}>
              <StatusIcon className="w-3 h-3" />{meta.label}
            </span>
            {f.anonymous && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest bg-plumSoft/10 text-plumSoft"><Shield className="w-3 h-3" />Anonymous</span>}
          </div>
          <p className="text-[14px] font-medium text-aubergine truncate">{f.subject}</p>
          <p className="text-[11px] text-plumSoft/70 mt-0.5">
            {isAdmin && !f.anonymous ? `${f.user_name} · ${f.user_chapter || "—"} · ` : ""}
            {new Date(f.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
          </p>
        </div>
        <div className="text-plumSoft shrink-0 pt-1">
          {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </div>
      </button>

      {open && (
        <div className="border-t border-aubergine/[0.06] px-5 py-4 bg-[#FAF8F5]/60">
          <p className="text-[13px] text-plumSoft/90 whitespace-pre-wrap leading-relaxed">{f.message}</p>

          {isAdmin && (
            <div className="mt-5 grid md:grid-cols-2 gap-5">
              <div>
                <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">Status</label>
                <div className="flex flex-wrap gap-2">
                  {["new", "reviewed", "resolved"].map(s => (
                    <button
                      key={s}
                      data-testid={`fb-status-${s}-${f.id}`}
                      onClick={() => onStatus(f.id, s)}
                      className={`px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest border transition-colors ${
                        f.status === s
                          ? "bg-aubergine text-ivory border-aubergine"
                          : "border-aubergine/20 text-plum hover:bg-aubergine/[0.04]"
                      }`}
                    >{s}</button>
                  ))}
                  <button
                    onClick={() => onDelete(f.id)}
                    data-testid={`fb-delete-${f.id}`}
                    className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] uppercase tracking-widest border border-rose/30 text-rose hover:bg-rose/[0.06]"
                  >
                    <Trash2 className="w-3 h-3" />Delete
                  </button>
                </div>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-[0.14em] text-plumSoft/70 mb-1.5">Internal notes (admin only)</label>
                <textarea
                  data-testid={`fb-notes-${f.id}`}
                  rows={3}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full px-3 py-2 rounded-lg bg-white border border-aubergine/[0.08] text-[12px] text-aubergine focus:border-aubergine/40 focus:outline-none"
                  placeholder="Add private notes about how this was handled…"
                />
                <button
                  onClick={() => onNotes(f.id, notes)}
                  data-testid={`fb-notes-save-${f.id}`}
                  className="mt-2 text-[11px] uppercase tracking-widest text-aubergine hover:text-plum"
                >
                  Save notes
                </button>
              </div>
            </div>
          )}

          {!isAdmin && f.admin_notes && (
            <div className="mt-4 p-3 rounded-lg bg-white border border-aubergine/[0.08]">
              <p className="text-[10px] uppercase tracking-widest text-plumSoft/70 mb-1">Admin response</p>
              <p className="text-[12px] text-plumSoft/90 whitespace-pre-wrap">{f.admin_notes}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
